"use client";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/navigation";
import "swiper/css/pagination";

import clsx from "clsx";
import { useEffect, useRef } from "react";
import { register } from "swiper/element/bundle";
import type { SwiperOptions } from "swiper/types";

import PC from "@/components/atoms/ProductCard";
import type { ProductCard } from "@/lib/centra/formatters/formatProductCards/formatProductCard";

import ArrowLeft from "@/components/icons/ArrowLeft";

import { ArrowRight } from "@/components/icons/ArrowRight";
import { useState } from "react";
import styles from "./product-slider.module.css";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "swiper-container": any;
      "swiper-slide": any;
    }
  }
}

type Slider = {
  initialize: () => void;
};
let sliderIdCounter = 0;
const Slider = ({
  cards,
  cardsPerRow,
}: {
  cards: { card: ProductCard; tag: string }[];
  cardsPerRow?: number;
}) => {
  register();
  const swiperRef = useRef<HTMLElement>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [sliderId] = useState(() => `slider-${++sliderIdCounter}`);

  useEffect(() => {
    const swiperEl = swiperRef.current;
    if (!swiperEl) return;

    const params: SwiperOptions = {
      navigation: {
        nextEl: `.slider-${sliderId} .custom-swiper-button-next`,
        prevEl: `.slider-${sliderId} .custom-swiper-button-prev`,
      },
      initialSlide: 0,
      scrollbar: {
        el: `.slider-${sliderId} .swiper-scrollbar`,
        draggable: true,
      },
      breakpoints: {
        0: { slidesPerView: 1.2, spaceBetween: 6 },
        640: { slidesPerView: 2, spaceBetween: 32 },
        1024: { slidesPerView: 3, spaceBetween: 32 },
      },
    };

    Object.assign(swiperEl, params);

    swiperEl.addEventListener("swiperinit", (e: any) => {
      const swiperInstance = e.detail[0];

      setIsBeginning(swiperInstance.isBeginning);
      setIsEnd(swiperInstance.isEnd);

      swiperInstance.on("slideChange", () => {
        setIsBeginning(swiperInstance.isBeginning);
        setIsEnd(swiperInstance.isEnd);
      });
    });

    (swiperEl as any).initialize();
  }, [sliderId]);

  return (
    <div className={clsx(styles.root, "product-slider", `slider-${sliderId}`)}>
      <div className={styles.navigationWrapper}>
        <button className="custom-swiper-button-prev" disabled={isBeginning}>
          <ArrowLeft />
        </button>
        <button className="custom-swiper-button-next" disabled={isEnd}>
          <ArrowRight />
        </button>
      </div>

      <swiper-container init="false" ref={swiperRef}>
        {cards.map((card, i) => (
          <swiper-slide key={`${card.card?.core?.slug}-${i}`}>
            <PC key={i} product={card.card} cardsPerRow={cardsPerRow} />
          </swiper-slide>
        ))}
      </swiper-container>
      <div className="scrollbar-wrapper">
        <div className="swiper-scrollbar"></div>
      </div>
    </div>
  );
};

const ProductSlider = ({
  cards,
  cardsPerRow,
}: {
  cards: {
    card: ProductCard;
    tag: string;
  }[];
  cardsPerRow?: number;
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={rootRef} className={styles["slider-wrapper"]}>
      <Slider cards={cards} />

      <div className={clsx(styles.root, styles.column__root, "column-root")}>
        {cards?.map((card, i) => (
          <PC key={i} product={card.card} cardsPerRow={cardsPerRow} />
        ))}
      </div>
    </div>
  );
};

export default ProductSlider;
